import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { userService } from "../services/userService";
import { toast } from "../hooks/use-toast";
import { User, Upload, Mail, Phone, Briefcase, Hash, Building } from "lucide-react";

const Profile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const currentUserId = user?.id || user?.userId;
        if (currentUserId) {
          const data = await userService.getById(currentUserId);
          setProfileData(data);
          // Assuming an imaginary API field until actual one is available
          // setProfileImage(data.profilePicture);
        }
      } catch (err) {
        console.error("Error fetching profile", err);
        if (user) {
          setProfileData({
            id: user.id || 1,
            username: user.username || user.name,
            email: user.email,
            firstName: user.firstName || user.name?.split(' ')[0] || 'User',
            middleName: user.middleName || '',
            lastName: user.lastName || user.name?.split(' ')[1] || '',
            mobile: user.mobile || '9876543210',
            designation: user.designation || user.role,
            department: user.department || 'Operations',
            company: user.company || 'TechApurva'
          });
        } else {
          toast({
            title: "Error",
            description: "Failed to load profile data",
            variant: "destructive",
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
        toast({
          title: "Profile Picture Updated",
          description: "Your profile picture has been updated locally.",
        });
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="text-gray-500">Loading profile...</div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="text-gray-500">Failed to load profile data.</div>
      </div>
    );
  }

  // Format middle name to exclude filler text like "no"
  const isInvalidMiddle = !profileData.middleName || ["no", "none", "n/a", "null"].includes(String(profileData.middleName).toLowerCase().trim());
  const validMiddle = isInvalidMiddle ? null : profileData.middleName;
  const fullName = [profileData.firstName, validMiddle, profileData.lastName].filter(Boolean).join(" ").trim() || profileData.username || "User";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">My Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            
            {/* Left Column: Avatar & Upload */}
            <div className="flex flex-col items-center space-y-4">
              <div 
                className="w-32 h-32 rounded-full bg-blue-100 flex items-center justify-center border-4 border-white shadow-lg overflow-hidden relative group cursor-pointer"
                onClick={handleImageClick}
              >
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-16 h-16 text-blue-600" />
                )}
                <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Upload className="w-6 h-6 text-white mb-1" />
                  <span className="text-xs text-white font-medium">Upload</span>
                </div>
              </div>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                accept="image/*" 
                className="hidden" 
              />
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900">{fullName}</h2>
                <p className="text-sm text-gray-500">{profileData.designation || "Employee"}</p>
              </div>
            </div>

            {/* Right Column: Details */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 w-full mt-4 md:mt-0">
              
              <div className="space-y-2">
                <Label className="text-gray-500 flex items-center">
                  <Mail className="w-4 h-4 mr-2" /> Email Address
                </Label>
                <div className="font-medium text-gray-900 p-2 bg-gray-50 rounded-md border border-gray-100">
                  {profileData.email || profileData.username || "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500 flex items-center">
                  <Phone className="w-4 h-4 mr-2" /> Phone Number
                </Label>
                <div className="font-medium text-gray-900 p-2 bg-gray-50 rounded-md border border-gray-100">
                  {profileData.mobile || "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500 flex items-center">
                  <Hash className="w-4 h-4 mr-2" /> Employee ID
                </Label>
                <div className="font-medium text-gray-900 p-2 bg-gray-50 rounded-md border border-gray-100">
                  {profileData.employeeId || "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500 flex items-center">
                  <Briefcase className="w-4 h-4 mr-2" /> Designation
                </Label>
                <div className="font-medium text-gray-900 p-2 bg-gray-50 rounded-md border border-gray-100">
                  {profileData.designation || "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500 flex items-center">
                  <Building className="w-4 h-4 mr-2" /> Department
                </Label>
                <div className="font-medium text-gray-900 p-2 bg-gray-50 rounded-md border border-gray-100">
                  {profileData.department || "Not specified"}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-500 flex items-center">
                  <User className="w-4 h-4 mr-2" /> Roles
                </Label>
                <div className="font-medium text-gray-900 p-2 bg-gray-50 rounded-md border border-gray-100 text-sm uppercase">
                  {profileData.userRoles?.join(", ") || "No Roles"}
                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
